import { useRef, useState } from 'react';
import { ChevronRight, ChevronsLeft, ChevronsRight, Lock, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation } from 'react-router-dom';

import {
  BopsLogo,
  BopsTitleLogo,
  ClientLogoIscpg,
  gs1_inventario_inteligente_logo,
  gs1_mexico_logo_sm,
  logyca_logo,
  logyca_logo_small,
} from '@Assets';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@Components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@Components/ui-v2/sidebar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@Components/ui-v2/tooltip';
import config from '../../config';
import ModalLogout from '../ModalLogout/ModalLogout';
import ModalResourceVideos, { ModalResourceVideosRef } from '../ModalResourceVideos/modalResourceVideos.container';
import SidebarResizeRail from './SidebarResizeRail';
import { MenuItem } from './types';
import { useSidebarExpandState } from './useSidebarExpandState';

const CUSTOM_LOGOS = {
  iscpg: { src: ClientLogoIscpg, collapsed: { h: 42, w: 42 } },
} as const;

const isPathActive = (link: string | undefined, pathname: string, search: string): boolean => {
  if (!link) return false;
  if (link.includes('?')) return `${pathname}${search}` === link;
  return !!matchPath(pathname, link);
};

interface PlatformSidebarProps {
  menuItems: MenuItem[];
  onLogout: () => void;
  showResources: boolean;
  logo?: string;
  locked?: boolean;
}

const PlatformSidebar = ({ menuItems, onLogout, showResources, logo, locked = false }: PlatformSidebarProps) => {
  const { t } = useTranslation();
  const { open, toggleSidebar } = useSidebar();
  const location = useLocation();
  const pathname = decodeURIComponent(location.pathname);
  const modalResourceRef = useRef<ModalResourceVideosRef>(null);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const { isOpen, setOpen } = useSidebarExpandState();

  const renderLogo = () => {
    // All non-bops logos were trimmed to their visible bounds so they no longer
    // need the h-20 padding boost. h-12 keeps roughly square assets (iscpg)
    // weighted similarly to the bops wordmark at h-9.
    if (config.isGs1Flavor) {
      return open ? (
        <img src={gs1_inventario_inteligente_logo} alt="inventario inteligente" className="h-16 w-auto" />
      ) : (
        <img src={gs1_mexico_logo_sm} alt="gs1mex" className="size-7" />
      );
    }
    if (config.isLogycaFlavor) {
      // Light sidebar across every flavor now, so use the black-letter logyca
      // wordmark. The asset was trimmed to its visible bounds (1391×387, no
      // dead padding) so h-9 reads at the same visual height as the bops
      // BopsTitleLogo wordmark — keeping the header rhythm consistent across
      // flavors.
      return open ? (
        <img src={logyca_logo} alt="logyca" className="h-9 w-auto" />
      ) : (
        <img src={logyca_logo_small} alt="logyca" className="size-7" />
      );
    }
    if (logo && logo in CUSTOM_LOGOS) {
      const cfg = CUSTOM_LOGOS[logo as keyof typeof CUSTOM_LOGOS];
      return open ? (
        <img src={cfg.src} alt="account" className="h-12 w-auto" />
      ) : (
        <img src={cfg.src} alt="account" height={cfg.collapsed.h} width={cfg.collapsed.w} />
      );
    }
    // BopsTitleLogo's wordmark uses currentColor — keep the bops text dark on
    // the light sidebar. If `.sidebar-dark` is re-activated later, swap this
    // to `text-white` (or add a `[.sidebar-dark_&]:text-white` variant).
    return open ? <BopsTitleLogo className="h-9 w-auto text-gray-900" /> : <BopsLogo className="size-9" />;
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.type === 'videos') modalResourceRef.current?.showModal();
    else item.onClick?.();
  };

  const renderExtraIcon = (item: MenuItem) => {
    if (!item.extraIcon) return null;
    const trigger = (
      <span
        role={item.extraIconOnClick ? 'button' : undefined}
        onClick={item.extraIconOnClick}
        className="ml-auto inline-flex shrink-0 items-center group-data-[collapsible=icon]:hidden">
        {item.extraIcon}
      </span>
    );
    if (!item.extraIconTooltip) return trigger;
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right">{item.extraIconTooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderInner = (item: MenuItem) => (
    <>
      {item.icon && <item.icon />}
      <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>
      {renderExtraIcon(item)}
    </>
  );

  /** Top-level item with no submenu — link, anchor or button. */
  const renderLeaf = (item: MenuItem) => {
    const active = isPathActive(item.link, pathname, location.search);
    if (item.externalLink) {
      return (
        <SidebarMenuButton asChild isActive={active}>
          <a href={item.externalLink} target="_blank" rel="noreferrer">
            {renderInner(item)}
          </a>
        </SidebarMenuButton>
      );
    }
    if (item.link) {
      return (
        <SidebarMenuButton asChild isActive={active}>
          <Link to={item.link}>{renderInner(item)}</Link>
        </SidebarMenuButton>
      );
    }
    return (
      <SidebarMenuButton isActive={active} onClick={() => handleItemClick(item)}>
        {renderInner(item)}
      </SidebarMenuButton>
    );
  };

  /** Submenu leaf — same as renderLeaf but uses SidebarMenuSubButton.
   * Intentionally omits the icon — submenu items show only the label for a flatter, less noisy hierarchy. */
  const renderSubLeaf = (item: MenuItem) => {
    const active = isPathActive(item.link, pathname, location.search);
    const inner = (
      <>
        <span>{item.label}</span>
        {renderExtraIcon(item)}
      </>
    );
    if (item.externalLink) {
      return (
        <SidebarMenuSubButton asChild isActive={active}>
          <a href={item.externalLink} target="_blank" rel="noreferrer">
            {inner}
          </a>
        </SidebarMenuSubButton>
      );
    }
    if (item.link) {
      return (
        <SidebarMenuSubButton asChild isActive={active}>
          <Link to={item.link}>{inner}</Link>
        </SidebarMenuSubButton>
      );
    }
    return (
      <SidebarMenuSubButton isActive={active} onClick={() => handleItemClick(item)}>
        {inner}
      </SidebarMenuSubButton>
    );
  };

  /** Top-level item — leaf or collapsible parent (shadcn sidebar-07 pattern). */
  /**
   * Render a single leaf menu item — used for both standalone top-level items
   * and items inside a labeled group section.
   */
  const renderLeafItem = (item: MenuItem) => (
    <SidebarMenuItem key={item.key ?? item.label}>{renderLeaf(item)}</SidebarMenuItem>
  );

  /**
   * Second-level submenu item — supports one more level of Collapsible nesting
   * (e.g. Monitor → Group → Dashboard). When the item has no submenu it falls
   * back to a plain SidebarMenuSubButton via renderSubLeaf.
   */
  const renderSubNested = (item: MenuItem) => {
    if (!item.submenu) {
      return <SidebarMenuSubItem key={item.key ?? item.label}>{renderSubLeaf(item)}</SidebarMenuSubItem>;
    }
    // All collapsible items in the menu builder carry an explicit key — label is a safety-net only.
    const subKey = item.key ?? item.label;
    return (
      <SidebarMenuSubItem key={subKey}>
        <Collapsible
          open={isOpen(subKey, item.defaultOpen)}
          onOpenChange={(v) => setOpen(subKey, v)}
          className="group/subcollapsible w-full">
          <CollapsibleTrigger asChild>
            <SidebarMenuSubButton className="w-full">
              <span>{item.label}</span>
              <ChevronRight className="ml-auto !size-3 opacity-50 transition-transform group-data-[state=open]/subcollapsible:rotate-90 group-data-[state=open]/subcollapsible:opacity-100" />
            </SidebarMenuSubButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.submenu.map((child) => (
                <SidebarMenuSubItem key={child.key ?? child.label}>{renderSubLeaf(child)}</SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuSubItem>
    );
  };

  /**
   * Render a nested submenu item — Monitor with multiple dashboards / groups
   * is the main consumer. Uses Collapsible and recurses into renderSubNested
   * for one extra level so dashboard groups stay collapsible.
   */
  const renderNestedItem = (item: MenuItem) => {
    if (!item.submenu) return renderLeafItem(item);
    // All collapsible items in the menu builder carry an explicit key — label is a safety-net only.
    const nestedKey = item.key ?? item.label;
    return (
      <Collapsible
        key={nestedKey}
        open={isOpen(nestedKey, item.defaultOpen)}
        onOpenChange={(v) => setOpen(nestedKey, v)}
        asChild
        className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton className="font-normal">
              {item.icon && <item.icon />}
              <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>
              <ChevronRight className="ml-auto !size-3.5 opacity-50 transition-[transform,opacity] group-hover/menu-item:opacity-100 group-data-[state=open]/collapsible:rotate-90 group-data-[state=open]/collapsible:opacity-100 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>{item.submenu.map(renderSubNested)}</SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  };

  // Monitor (any menu item with key starting with "monitor") is special: it
  // always renders first at the top of the sidebar as a leaf or collapsible
  // parent, never as a labeled section. Everything else follows the regular
  // standalone / labeled-section split.
  const isMonitorItem = (item: MenuItem) => item.key?.startsWith('monitor') ?? false;
  const monitorItem = menuItems.find(isMonitorItem);
  const restItems = menuItems.filter((item) => !isMonitorItem(item));
  const standaloneItems = restItems.filter((item) => !item.submenu);
  const groupedItems = restItems.filter((item) => item.submenu);
  const topItems = monitorItem ? [monitorItem, ...standaloneItems] : standaloneItems;

  return (
    <Sidebar collapsible="icon">
      <SidebarResizeRail />
      <SidebarHeader className="px-4 pb-4 pt-6 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:pt-5">
        <div className="flex items-center justify-center">{renderLogo()}</div>
      </SidebarHeader>

      <SidebarContent className="relative">
        <div className={locked ? 'pointer-events-none select-none blur-[2px] saturate-50' : undefined}>
          {topItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>{topItems.map(renderNestedItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
          {groupedItems.map((group) => (
            <SidebarGroup key={group.key ?? group.label}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{group.submenu!.map(renderNestedItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </div>
        {locked && (
          <div className="absolute inset-0 flex items-center justify-center bg-sidebar/60 px-3 backdrop-blur-[2px]">
            {open && (
              <div className="flex flex-col items-center gap-2 rounded-lg border bg-white/95 p-4 text-center shadow-sm">
                <Lock className="size-4 text-gray-500" />
                <span className="text-xs leading-snug text-gray-700">
                  {t('Sidebar.LockedPreviewMessage', {
                    defaultValue: 'Navigation unlocks once your workspace is ready',
                  })}
                </span>
              </div>
            )}
          </div>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setLogoutModalOpen(true)}
              className="font-normal text-error-600 hover:bg-error-50 hover:text-error-700 [&>svg]:text-error-600 hover:[&>svg]:text-error-700 [.sidebar-dark_&]:text-error-400 [.sidebar-dark_&]:[&>svg]:text-error-400 [.sidebar-dark_&]:hover:bg-error-500/15 [.sidebar-dark_&]:hover:text-error-300 [.sidebar-dark_&]:hover:[&>svg]:text-error-300">
              <LogOut />
              <span className="group-data-[collapsible=icon]:hidden">{t('Sidebar.Logout')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={toggleSidebar} className="font-normal">
              {open ? <ChevronsLeft /> : <ChevronsRight />}
              <span className="group-data-[collapsible=icon]:hidden">{t('Sidebar.Collapse')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      {showResources && <ModalResourceVideos ref={modalResourceRef} />}

      <ModalLogout
        isOpen={logoutModalOpen}
        onCancel={() => setLogoutModalOpen(false)}
        onLogout={() => {
          setLogoutModalOpen(false);
          onLogout();
        }}
      />
    </Sidebar>
  );
};

export default PlatformSidebar;
